import React, { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { ProfileAvatar, ProfileAvatarHeroWithFallback } from "./ProfileAvatar";
import { ProfileLink } from "./ProfileLink";
import { useProfileGlobalModal } from "./ProfileGlobalModalContext";
import { useTraderDataFetcher } from "../../Data/TraderDataFetcherContext";
import { useVisibleStaleFallback } from "../../hooks/useSocialSyncRefresh";
import { useUser } from "../../Data/UserContext";
import { getAuthHeaders } from "../../utils/authHeaders";
import { trackChallengeFriendClicked, trackChallengeGameSelected, trackFriendRequestCancelled, trackFriendRequestResponse, trackFriendRequestSent } from "../../utils/frontendTrackingEvents";
import {
  addMutedFriend,
  getMutedFriendIdsSet,
  MUTED_FRIENDS_CHANGED_EVENT,
  removeMutedFriend,
} from "../../utils/mutedFriendsStorage";
import { getCountryNameByCode } from "../Account/Countries";
import { resolveAvatarUrl, toProfileHeroAvatarUrl } from "../../utils/sessionUser";
import { ProfileFavouritesStrip, type ProfileFavChip } from "./ProfileFavouritesStrip";
import { parseFavSportsFromStorage } from "./profileFavSportIcons";
import {
  parseFavFootballFromStorage,
  favFootballLabel,
  favFootballLongLabel,
} from "./profileFavFootballTeams";
import { parseFavIplFromStorage, favIplLabel, favIplLongLabel } from "./profileFavIplTeams";
import { parseFavCricketIntFromStorage, favCricketIntLabel } from "./profileFavCricketIntTeams";
import { normalizeFavourites, parseFavTeamsFromStorage, slugifyTeamName } from "../../utils/favouriteTeams";
import { canonicalTeamSlug, resolveCanonicalTeam } from "../../utils/cricketTeamCanonical";
import {
  canSeeDisplayName,
  optionalDisplayName,
  playerHandleName,
} from "../../utils/friendDisplayName";
import { ProfileNameStack } from "./ProfileNameStack";
import { parseFavCricketersFromStorage, favCricketerLabel } from "./profileFavCricketers";
import { CareerStatsStack } from "./CareerStatsStack";
import { CAREER_STAT_ICONS } from "./CareerStatCardHeader";
import { coercePublicCareer, type CareerSummaryPayload } from "./careerSummary";
import type { ProfileModalLocationState } from "../../utils/profileModalNavigation";
import { whenModalHistoryIdle } from "../../utils/modalHistoryBack";
import SquaresChallengeFriendModal, {
  type SquaresChallengeFriend,
} from "../MyZone/SquaresChallengeFriendModal";
import ChallengeGameSelectPopover from "../MyZone/ChallengeGameSelectPopover";
import BullionPrivateContestsFriendModal from "../MyZone/BullionPrivateContestsFriendModal";
import AreYouSure from "../Modals/AreYouSureModal";
import { ThreeDotsCircleIcon } from "../../icons/ThreeDotsCircleIcon/ThreeDotsCircleIcon";
import { ModalCloseButton } from "../ModalCloseIcon";
import { formatMatchNameAbbreviated } from "../../utils/matchNameDisplay";
import { buildMatchCompleteHref } from "../Matches/matchesHubNavigation";
import {
  hasReadableActivityMatchName,
  traderActivityMatchHref,
} from "../../utils/traderActivityNavigation";
import FriendActivityTimeline, {
  ACTIVITY_ACTION_BUTTON,
  mapDigestRows,
  type FriendDigest,
  type FriendListEntry,
  type MySquareGame,
  type MyTradeGame,
} from "../MyZone/FriendActivityTimeline";
import { useLiveSquaresChallengeFromLobby } from "../../hooks/useLiveSquaresChallengeFromLobby";
import { useGamesEnabled } from "../../hooks/useGamesEnabled";
import { openCreateLeagueModal, useLeaguesAccess } from "../Leagues/hooks/useMyLeaguesLists";
import { formatXp, formatXpHistoryReasonDetail, XP_ICON_SRC } from "../../utils/xp";

type PublicFriendProfileAction = "mute" | "remove" | "unmute";

export type FriendshipStatus = "none" | "friends" | "pending_sent" | "pending_received" | "blocked";

export type PublicFriendPreview = {
  userId: number;
  player_id?: string | null;
  player_name?: string | null;
  username?: string;
  profile_pic?: string | null;
  xp_week_crown?: string | null;
  avatar_frame_url?: string | null;
  avatar_frame_small_url?: string | null;
};

export type RecentActivityKind = "trader" | "squares" | "squares_active" | "sixes" | "xp";
const PUBLIC_PROFILE_CACHE_TTL_MS = 45_000;

function publicProfileCacheKey(identifier: string, viewerUserId?: string | number | null): string {
  const viewer = viewerUserId != null && String(viewerUserId).trim() !== "" ? String(viewerUserId) : "anon";
  return `c8_public_profile_${viewer}_${identifier.trim().toLowerCase()}`;
}

/** Display-only strip for hidden / bot public profiles (403 preview or client-derived). */
type PublicProfileUnavailableIdentity = {
  player_name?: string | null;
  username?: string | null;
  profile_pic?: string | null;
  avatar_url?: string | null;
  avatar_color?: string | null;
  first_letter?: string | null;
  xp_week_crown?: string | null;
  avatar_frame_url?: string | null;
  avatar_frame_small_url?: string | null;
};

function parseRestrictedPreviewFromApi(raw: unknown): PublicProfileUnavailableIdentity | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  return {
    player_name: typeof o.player_name === "string" ? o.player_name : null,
    username: typeof o.username === "string" ? o.username : null,
    profile_pic: typeof o.profile_pic === "string" ? o.profile_pic : null,
    avatar_url: typeof o.avatar_url === "string" ? o.avatar_url : null,
    avatar_color: typeof o.avatar_color === "string" ? o.avatar_color : null,
    first_letter: typeof o.first_letter === "string" ? o.first_letter : null,
    xp_week_crown: typeof o.xp_week_crown === "string" ? o.xp_week_crown : null,
    avatar_frame_url: typeof o.avatar_frame_url === "string" ? o.avatar_frame_url : null,
    avatar_frame_small_url: typeof o.avatar_frame_small_url === "string" ? o.avatar_frame_small_url : null,
  };
}

function readPublicProfileCache(
  identifier: string,
  viewerUserId?: string | number | null
): {
  profile: PublicProfileData | null;
  privateMessage: string | null;
  restrictedPreview: PublicProfileUnavailableIdentity | null;
} | null {
  try {
    const raw = sessionStorage.getItem(publicProfileCacheKey(identifier, viewerUserId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      ts?: number;
      profile?: PublicProfileData | null;
      privateMessage?: string | null;
      restrictedPreview?: PublicProfileUnavailableIdentity | null;
    };
    const ts = typeof parsed.ts === "number" ? parsed.ts : 0;
    if (!ts || Date.now() - ts > PUBLIC_PROFILE_CACHE_TTL_MS) return null;
    return {
      profile: parsed.profile ?? null,
      privateMessage: parsed.privateMessage ?? null,
      restrictedPreview: parsed.restrictedPreview ?? null,
    };
  } catch {
    return null;
  }
}

function writePublicProfileCache(
  identifier: string,
  viewerUserId: string | number | null | undefined,
  payload: {
    profile: PublicProfileData | null;
    privateMessage: string | null;
    restrictedPreview?: PublicProfileUnavailableIdentity | null;
  }
): void {
  try {
    sessionStorage.setItem(
      publicProfileCacheKey(identifier, viewerUserId),
      JSON.stringify({ ts: Date.now(), ...payload })
    );
  } catch {
    // ignore storage errors
  }
}

export type RecentActivityItem = {
  kind?: RecentActivityKind;
  playing?: boolean;
  created_at?: string | null;
  over_id?: number;
  settled_at: string | null;
  outcome: string | null;
  sport: string;
  match_id: string | null;
  profit: number;
  /** Display name for the fixture (from squares_match / fixtures / fallback). */
  match_name?: string | null;
  /** Squares over number when known (active or settled). */
  over_number?: number | null;
  xp_amount?: number;
  xp_reason?: string | null;
};

interface PublicProfileData {
  /** Legacy; reserved bot payloads use `userId <= 0` instead. */
  synthetic_bot?: boolean;
  userId: number;
  playerId?: string | null;
  player_id?: string | null;
  username?: string;
  player_name?: string | null;
  /** Present only for owner / accepted friends (API omits otherwise). */
  display_name?: string | null;
  avatar?: string | null;
  profile_pic?: string | null;
  avatar_url?: string | null;
  /** Letter tile (bots); optional for real users — computed client-side when absent. */
  avatar_color?: string | null;
  first_letter?: string | null;
  country?: string | null;
  created_at?: string | null;
  favourites?: {
    favsports?: string;
    favcricketers?: string;
    faviplteams?: string;
    favnationalteams?: string;
    favfootballteams?: string;
    favcustomcricketteams?: string;
  } | null;
  friendshipStatus?: FriendshipStatus;
  friendRequestId?: number;
  /** When false, others cannot send new friend requests (API default true). */
  accept_friend_requests?: boolean;
  /** Current-week XP podium crown overlay (gold / silver / bronze). */
  xp_week_crown?: string | null;
  avatar_frame_url?: string | null;
  avatar_frame_small_url?: string | null;
  games?: Array<{ name: string; values: Record<string, unknown> }>;
  friends_preview?: PublicFriendPreview[];
  career?: {
    trader: {
      trades: number;
      won: number;
      profit: number;
      total_winnings?: number;
      current_win_streak?: number;
      record_win_streak?: number;
      biggest_win?: number;
    };
    squares: { played: number; won: number; net_winnings: number };
    bullion?: { played: number; won: number; net_winnings: number };
    sixes?: { played: number; net_winnings: number; biggest_win?: number };
    leagues?: { played: number; won: number; net_winnings: number };
    mini_games?: CareerSummaryPayload["mini_games"];
    totals?: CareerSummaryPayload["totals"];
  } | null;
  recent_activity?: RecentActivityItem[];
  is_online?: boolean;
  online?: boolean;
  online_status?: string | boolean | null;
  presence?: { online?: boolean } | string | boolean | null;
}

/** Leaderboard / Squares listed bots: no real account (`userId` 0). */
function isNonUserPublicProfile(profile: PublicProfileData | null | undefined): boolean {
  if (!profile) return false;
  if (profile.synthetic_bot) return true;
  return Number(profile.userId) <= 0;
}

/**
 * Hidden / bot / not-found public profile — same header grid as a visible public profile
 * (OFFLINE + dot col 1, avatar + name col 2, modal close col 3); message in scroll body.
 */
function ProfileUnavailablePanel({
  message,
  onClose,
  identity,
}: {
  message: string;
  onClose: () => void;
  identity?: PublicProfileUnavailableIdentity | null;
}) {
  const hasIdentity = Boolean(
    identity &&
      (identity.player_name?.trim() ||
        identity.username?.trim() ||
        identity.profile_pic?.trim() ||
        identity.avatar_url?.trim())
  );
  const rawPhotoUrl = resolveAvatarUrl({
    profile_pic: identity?.profile_pic,
    avatar_url: identity?.avatar_url,
  });
  const resolvedHeroSrc = toProfileHeroAvatarUrl(rawPhotoUrl);
  const rawHadPhoto = Boolean(rawPhotoUrl && String(rawPhotoUrl).trim());
  const displayNameForAvatar = identity?.player_name || identity?.username || null;
  const displayName = hasIdentity
    ? (identity!.player_name || identity!.username || "Player").trim().toUpperCase() || "PLAYER"
    : null;

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
      <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-start gap-x-2 border-b border-white/10 px-3 py-1.5 sm:py-2">
        <div className="col-start-1 flex min-w-0 flex-col items-start gap-2 self-start">
          <span className="flex items-center gap-1.5 font-anton text-[8px] font-normal uppercase leading-[12px] tracking-normal text-white/55 [leading-trim:none]">
            <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-white/45" aria-hidden />
            OFFLINE
          </span>
        </div>
        <div className="col-start-2 flex max-w-[min(100%,11rem)] flex-col items-center gap-3 justify-self-center self-start sm:max-w-[13rem]">
          <div className="relative shrink-0">
            <ProfileAvatarHeroWithFallback
              resolvedSrc={resolvedHeroSrc}
              rawHadPhoto={rawHadPhoto}
              displayName={displayNameForAvatar}
              avatarColor={identity?.avatar_color}
              firstLetter={identity?.first_letter}
              xpWeekCrown={identity?.xp_week_crown}
              avatarFrameUrl={identity?.avatar_frame_url}
              avatarFrameSmallUrl={identity?.avatar_frame_small_url}
            />
          </div>
          {displayName ? (
            <h1 className="w-full max-w-full truncate text-center font-anton text-[20px] font-normal uppercase leading-[26px] tracking-normal text-white [leading-trim:none]">
              {displayName}
            </h1>
          ) : null}
        </div>
        <div className="col-start-3 justify-self-end self-start">
          <ModalCloseButton
            onClick={onClose}
            ariaLabel="Close profile"
            className="h-11 w-11 shrink-0 translate-x-1.5 -translate-y-1.5 rounded-none hover:bg-white/10 hover:opacity-100"
          />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-y-auto overscroll-contain px-3 pb-8 pt-3">
        <p className="max-w-md text-center text-sm text-white/80">{message}</p>
      </div>
    </div>
  );
}

function formatCoins(amount: number) {
  return new Intl.NumberFormat("en-IN").format(Math.round(amount));
}

function formatActivityRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
  return `${Math.floor(sec / 604800)}w`;
}

function profileIsOnlineNow(profile: PublicProfileData | null): boolean {
  if (!profile) return false;
  if (typeof profile.is_online === "boolean") return profile.is_online;
  if (typeof profile.online === "boolean") return profile.online;
  if (typeof profile.online_status === "boolean") return profile.online_status;
  if (typeof profile.online_status === "string") {
    const status = profile.online_status.trim().toLowerCase();
    if (status === "online") return true;
    if (status === "offline") return false;
  }
  if (typeof profile.presence === "boolean") return profile.presence;
  if (typeof profile.presence === "string") {
    const status = profile.presence.trim().toLowerCase();
    if (status === "online") return true;
    if (status === "offline") return false;
  }
  if (profile.presence && typeof profile.presence === "object" && typeof profile.presence.online === "boolean") {
    return profile.presence.online;
  }
  return false;
}

function gameValues(
  games: PublicProfileData["games"],
  name: string
): { played: number; lastwon: number; answered: number; questions: number } {
  const g = games?.find((x) => x.name === name);
  const v = g?.values || {};
  return {
    played: typeof v.played === "number" ? v.played : Number(v.played) || 0,
    lastwon: typeof v.lastwon === "number" ? v.lastwon : Number(v.lastwon) || 0,
    answered: typeof v.answered === "number" ? v.answered : Number(v.answered) || 0,
    questions: typeof v.questionstoanswer === "number" ? v.questionstoanswer : Number(v.questionstoanswer) || 0,
  };
}

interface PublicProfileProps {
  identifier: string;
  onClose: () => void;
}

export default function PublicProfile({ identifier, onClose }: PublicProfileProps) {
  const { openOwnProfile, dismissProfileModalForNavigation } = useProfileGlobalModal();
  const { fetchMyGamesOverview } = useTraderDataFetcher();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const profileLinkBackground: ProfileModalLocationState = {
    from:
      (location.state as ProfileModalLocationState | null)?.from ?? {
        pathname: location.pathname,
        search: location.search,
      },
  };
  const navigateFromProfileOverlay = useCallback(
    (to: string) => {
      dismissProfileModalForNavigation();
      navigate(to, { state: profileLinkBackground });
    },
    [dismissProfileModalForNavigation, navigate, profileLinkBackground]
  );
  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [privateMessage, setPrivateMessage] = useState<string | null>(null);
  const [restrictedPreview, setRestrictedPreview] = useState<PublicProfileUnavailableIdentity | null>(null);
  const [addFriendState, setAddFriendState] = useState<"idle" | "loading" | "error">("idle");
  const [removeFriendLoading, setRemoveFriendLoading] = useState(false);
  const [acceptRequestLoading, setAcceptRequestLoading] = useState(false);
  const [cancelRequestLoading, setCancelRequestLoading] = useState(false);
  const [squaresChallengeFriend, setSquaresChallengeFriend] = useState<SquaresChallengeFriend | null>(null);
  const [challengeGamePicker, setChallengeGamePicker] = useState<{
    friend: SquaresChallengeFriend;
    anchorRect: DOMRect;
  } | null>(null);
  const [bullionContestFriend, setBullionContestFriend] = useState<SquaresChallengeFriend | null>(null);
  const openChallengedMatchFromProfile = useCallback(
    (href: string) => {
      setChallengeGamePicker(null);
      setSquaresChallengeFriend(null);
      setBullionContestFriend(null);
      const background: ProfileModalLocationState = {
        from:
          (location.state as ProfileModalLocationState | null)?.from ?? {
            pathname: location.pathname,
            search: location.search,
          },
      };
      whenModalHistoryIdle(() => {
        dismissProfileModalForNavigation();
        navigate(href, { state: background });
      });
    },
    [dismissProfileModalForNavigation, location.pathname, location.search, location.state, navigate]
  );
  const [friendProfileMenuOpen, setFriendProfileMenuOpen] = useState(false);
  const [confirmFriendProfileAction, setConfirmFriendProfileAction] = useState<PublicFriendProfileAction | null>(
    null
  );
  const [profileFriendMuted, setProfileFriendMuted] = useState(false);
  const [muteFriendLoading, setMuteFriendLoading] = useState(false);
  const [friendFeedDigests, setFriendFeedDigests] = useState<FriendDigest[]>([]);
  const [friendLiveFriends, setFriendLiveFriends] = useState<FriendListEntry[]>([]);
  const [friendFeedMyTrades, setFriendFeedMyTrades] = useState<MyTradeGame[]>([]);
  const [friendFeedMySquares, setFriendFeedMySquares] = useState<MySquareGame[]>([]);
  const [friendFeedLoading, setFriendFeedLoading] = useState(false);
  const [friendFeedLoadingMore, setFriendFeedLoadingMore] = useState(false);
  const [friendFeedHasMore, setFriendFeedHasMore] = useState(false);
  const [friendFeedResultsLimit, setFriendFeedResultsLimit] = useState(10);
  const [profileRefreshTick, setProfileRefreshTick] = useState(0);

  const liveSquaresForChallenge = useLiveSquaresChallengeFromLobby({
    enabled: Boolean(
      user &&
        profile &&
        !isNonUserPublicProfile(profile) &&
        profile.friendshipStatus === "friends" &&
        Number(profile.userId) !== Number(user.userId)
    ),
  });
  const { isBullionEnabled } = useGamesEnabled();
  const leaguesAccess = useLeaguesAccess();
  const showLeaguesChallenge = leaguesAccess.ready && leaguesAccess.vip;
  const canChallengeFriend = isBullionEnabled || liveSquaresForChallenge || showLeaguesChallenge;

  const fetchProfile = useCallback(async (opts?: { silent?: boolean }) => {
    const requestedSilent = opts?.silent === true;
    if (!identifier?.trim()) {
      if (!requestedSilent) setLoading(false);
      return;
    }
    const cached = readPublicProfileCache(identifier, user?.userId);
    const silent = requestedSilent || Boolean(cached);
    if (!requestedSilent && cached) {
      setProfile(cached.profile);
      setPrivateMessage(cached.privateMessage);
      setRestrictedPreview(cached.restrictedPreview ?? null);
      setLoading(false);
    } else if (!silent) {
      setLoading(true);
      setPrivateMessage(null);
      setRestrictedPreview(null);
    }
    try {
      const url = `${process.env.API_URL}/account/public-profile/${encodeURIComponent(identifier.trim())}`;
      const res = await fetch(url, { method: "GET", headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setProfile(data);
        setPrivateMessage(null);
        setRestrictedPreview(null);
        writePublicProfileCache(identifier, user?.userId, {
          profile: data,
          privateMessage: null,
          restrictedPreview: null,
        });
      } else if (res.status === 403) {
        const data = (await res.json().catch(() => ({}))) as { message?: string; preview?: unknown };
        const pm =
          typeof data.message === "string" && data.message.trim()
            ? data.message
            : "This profile is not visible to others.";
        const preview = parseRestrictedPreviewFromApi(data.preview);
        setProfile(null);
        setPrivateMessage(pm);
        setRestrictedPreview(preview);
        writePublicProfileCache(identifier, user?.userId, {
          profile: null,
          privateMessage: pm,
          restrictedPreview: preview,
        });
      } else {
        if (!silent) {
          setProfile(null);
          setPrivateMessage(null);
          setRestrictedPreview(null);
        }
      }
    } catch {
      if (!silent) {
        setProfile(null);
        setPrivateMessage(null);
        setRestrictedPreview(null);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [identifier, user?.userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  /** Rare visible-only reconcile; primary updates from c8-* listeners below. */
  useVisibleStaleFallback(
    () => {
      void fetchProfile({ silent: true });
      setProfileRefreshTick((n) => n + 1);
    },
    Boolean(identifier?.trim())
  );

  useEffect(() => {
    let timer: number | null = null;
    const scheduleRefresh = () => {
      if (timer != null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        void fetchProfile({ silent: true });
        setProfileRefreshTick((n) => n + 1);
      }, 450);
    };
    window.addEventListener("c8-profile-dirty", scheduleRefresh);
    window.addEventListener("c8-activity-updated", scheduleRefresh);
    window.addEventListener("c8-my-games-dirty", scheduleRefresh);
    window.addEventListener("c8-friends-dirty", scheduleRefresh);
    window.addEventListener("c8-notifications-updated", scheduleRefresh);
    return () => {
      if (timer != null) window.clearTimeout(timer);
      window.removeEventListener("c8-profile-dirty", scheduleRefresh);
      window.removeEventListener("c8-activity-updated", scheduleRefresh);
      window.removeEventListener("c8-my-games-dirty", scheduleRefresh);
      window.removeEventListener("c8-friends-dirty", scheduleRefresh);
      window.removeEventListener("c8-notifications-updated", scheduleRefresh);
    };
  }, [fetchProfile]);

  useEffect(() => {
    if (profile) {
      document.title = `${playerHandleName(profile)} | Cricket8`;
    }
    return () => {
      document.title = "Cricket8";
    };
  }, [profile]);

  useEffect(() => {
    setFriendProfileMenuOpen(false);
    setProfileFriendMuted(false);
    setConfirmFriendProfileAction(null);
  }, [identifier]);

  useEffect(() => {
    const viewerId = user?.userId != null ? Number(user.userId) : null;
    if (viewerId == null || profile?.userId == null || isNonUserPublicProfile(profile)) {
      setProfileFriendMuted(false);
      return;
    }
    const sync = () => setProfileFriendMuted(getMutedFriendIdsSet(viewerId).has(Number(profile.userId)));
    sync();
    window.addEventListener(MUTED_FRIENDS_CHANGED_EVENT, sync);
    return () => window.removeEventListener(MUTED_FRIENDS_CHANGED_EVENT, sync);
  }, [user?.userId, profile?.userId]);

  useEffect(() => {
    setFriendFeedDigests([]);
    setFriendLiveFriends([]);
    setFriendFeedMyTrades([]);
    setFriendFeedMySquares([]);
    setFriendFeedHasMore(false);
    setFriendFeedResultsLimit(10);
    setFriendFeedLoadingMore(false);
    setFriendFeedLoading(false);
  }, [identifier]);

  useEffect(() => {
    const viewerId = user?.userId != null ? Number(user.userId) : null;
    if (viewerId == null || !profile) {
      setFriendFeedDigests([]);
      setFriendLiveFriends([]);
      setFriendFeedMyTrades([]);
      setFriendFeedMySquares([]);
      setFriendFeedLoading(false);
      return;
    }
    if (isNonUserPublicProfile(profile)) {
      setFriendFeedDigests([]);
      setFriendLiveFriends([]);
      setFriendFeedMyTrades([]);
      setFriendFeedMySquares([]);
      setFriendFeedLoading(false);
      return;
    }
    const viewingOwnProfile = Number(profile.userId) === viewerId;
    if (!viewingOwnProfile && profile.friendshipStatus !== "friends") {
      setFriendFeedDigests([]);
      setFriendLiveFriends([]);
      setFriendFeedMyTrades([]);
      setFriendFeedMySquares([]);
      setFriendFeedLoading(false);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const hasExistingFeedData =
      friendFeedDigests.length > 0 ||
      friendLiveFriends.length > 0 ||
      friendFeedMyTrades.length > 0 ||
      friendFeedMySquares.length > 0;
    const isIncrementalLoad = hasExistingFeedData;
    if (!isIncrementalLoad) setFriendFeedLoading(true);
    else setFriendFeedLoadingMore(true);
    const uid = profile.userId;
    const sessionToken = Cookies.get("c8_session_token");
    const actUrl = `${process.env.API_URL}/account/friends-activity?${new URLSearchParams({
      friend_id: String(uid),
      recent_days: "7",
      results_limit: String(friendFeedResultsLimit),
    })}`;
    void Promise.all([
      fetch(actUrl, { method: "GET", headers: getAuthHeaders(), signal: controller.signal }),
      sessionToken ? fetchMyGamesOverview() : Promise.resolve(null),
    ])
      .then(async ([actRes, myGamesPayload]) => {
        if (cancelled) return;
        if (actRes.ok) {
          const data = (await actRes.json()) as {
            live_friend?: FriendListEntry | null;
            activity?: unknown;
            has_more?: boolean;
          };
          setFriendFeedDigests(mapDigestRows(data));
          setFriendFeedHasMore(Boolean(data.has_more));
          const lf = data.live_friend;
          if (lf && typeof lf === "object" && lf.userId != null) {
            setFriendLiveFriends([lf]);
          } else {
            setFriendLiveFriends([]);
          }
        } else {
          setFriendFeedDigests([]);
          setFriendLiveFriends([]);
        }
        if (myGamesPayload?.active) {
          setFriendFeedMyTrades(
            Array.isArray(myGamesPayload.active.trades) ? (myGamesPayload.active.trades as MyTradeGame[]) : []
          );
          setFriendFeedMySquares(
            Array.isArray(myGamesPayload.active.squares) ? (myGamesPayload.active.squares as MySquareGame[]) : []
          );
        } else {
          setFriendFeedMyTrades([]);
          setFriendFeedMySquares([]);
        }
        if (!cancelled) {
          setFriendFeedLoading(false);
          setFriendFeedLoadingMore(false);
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        if (!cancelled) {
          setFriendFeedDigests([]);
          setFriendLiveFriends([]);
          setFriendFeedMyTrades([]);
          setFriendFeedMySquares([]);
          setFriendFeedHasMore(false);
          setFriendFeedLoading(false);
          setFriendFeedLoadingMore(false);
        }
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    fetchMyGamesOverview,
    profileRefreshTick,
    user?.userId,
    profile?.userId,
    profile?.friendshipStatus,
    profile?.synthetic_bot,
    friendFeedDigests.length,
    friendLiveFriends.length,
    friendFeedMyTrades.length,
    friendFeedMySquares.length,
    friendFeedResultsLimit,
  ]);

  useEffect(() => {
    if (!friendProfileMenuOpen) return;
    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-public-friend-actions]")) return;
      setFriendProfileMenuOpen(false);
    };
    document.addEventListener("pointerdown", handleDocumentPointerDown);
    return () => document.removeEventListener("pointerdown", handleDocumentPointerDown);
  }, [friendProfileMenuOpen]);

  const handleAddFriend = useCallback(async () => {
    if (!user) {
      const returnTo = window.location.pathname + window.location.search;
      navigate(`/login?back=${encodeURIComponent(returnTo)}`, { replace: true });
      return;
    }
    if (isNonUserPublicProfile(profile)) return;
    if (profile?.userId === user.userId) {
      openOwnProfile();
      return;
    }
    setAddFriendState("loading");
    try {
      const res = await fetch(`${process.env.API_URL}/account/friend-request/send`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ receiver_id: profile!.userId }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        trackFriendRequestSent({
          user_id: user.userId,
          sender_user_id: user.userId,
          receiver_user_id: profile!.userId,
          pathname: location.pathname,
        });
        setProfile((p) => (p ? { ...p, friendshipStatus: "pending_sent" as const } : null));
      } else {
        const err = data?.error;
        if (err === "already_friends") setProfile((p) => (p ? { ...p, friendshipStatus: "friends" as const } : null));
        else if (err === "pending" || err === "pending_received")
          setProfile((p) =>
            p ? { ...p, friendshipStatus: err === "pending_received" ? "pending_received" : "pending_sent" } : null
          );
        else if (err === "requests_disabled") {
          setProfile((p) => (p ? { ...p, accept_friend_requests: false } : null));
        } else setAddFriendState("error");
      }
    } catch {
      setAddFriendState("error");
    } finally {
      setAddFriendState((s) => (s === "loading" ? "idle" : s));
    }
  }, [user, profile?.userId, profile?.synthetic_bot, navigate, openOwnProfile, location.pathname]);

  const handleRemoveFriend = useCallback(async () => {
    if (!user || !profile || profile.friendshipStatus !== "friends") return;
    setRemoveFriendLoading(true);
    try {
      const res = await fetch(`${process.env.API_URL}/account/friend/remove`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ friend_id: profile.userId }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (res.ok && data.success) {
        if (user?.userId != null && profile?.userId != null) {
          removeMutedFriend(Number(user.userId), Number(profile.userId));
        }
        setProfile((p) => (p ? { ...p, friendshipStatus: "none" as const } : null));
        setFriendProfileMenuOpen(false);
        setProfileFriendMuted(false);
      }
    } finally {
      setRemoveFriendLoading(false);
    }
  }, [user, profile?.userId, profile?.friendshipStatus]);

  const handleMuteProfileFriend = useCallback(async () => {
    if (!profile || user?.userId == null) return;
    setMuteFriendLoading(true);
    try {
      addMutedFriend(Number(user.userId), Number(profile.userId));
      setProfileFriendMuted(true);
    } finally {
      setMuteFriendLoading(false);
    }
  }, [profile, user?.userId]);

  const handleUnmuteProfileFriend = useCallback(async () => {
    if (!profile || user?.userId == null) return;
    setMuteFriendLoading(true);
    try {
      removeMutedFriend(user.userId, profile.userId);
      setProfileFriendMuted(false);
    } finally {
      setMuteFriendLoading(false);
    }
  }, [profile, user?.userId]);

  const closeConfirmFriendProfileAction = useCallback(() => {
    setConfirmFriendProfileAction(null);
  }, []);

  const confirmSelectedFriendProfileAction = useCallback(async () => {
    if (!confirmFriendProfileAction) return;
    if (confirmFriendProfileAction === "remove") {
      await handleRemoveFriend();
    } else if (confirmFriendProfileAction === "unmute") {
      await handleUnmuteProfileFriend();
    } else {
      await handleMuteProfileFriend();
    }
    setConfirmFriendProfileAction(null);
  }, [
    confirmFriendProfileAction,
    handleRemoveFriend,
    handleMuteProfileFriend,
    handleUnmuteProfileFriend,
  ]);

  const handleAcceptFriendRequest = useCallback(async () => {
    if (!user || !profile || profile.friendshipStatus !== "pending_received" || profile.friendRequestId == null) return;
    setAcceptRequestLoading(true);
    try {
      const res = await fetch(`${process.env.API_URL}/account/friend-request/accept`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ request_id: profile.friendRequestId }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (res.ok && data.success) {
        if (user?.userId != null && profile?.userId != null) {
          trackFriendRequestResponse({
            user_id: user.userId,
            response: "accepted",
            friend_request_sender_user_id: profile.userId,
            friend_request_receiver_user_id: user.userId,
            pathname: location.pathname,
          });
        }
        setProfile((p) => (p ? { ...p, friendshipStatus: "friends" as const } : null));
      }
    } finally {
      setAcceptRequestLoading(false);
    }
  }, [user, profile?.friendshipStatus, profile?.friendRequestId, profile?.userId, location.pathname]);

  const handleCancelFriendRequest = useCallback(async () => {
    if (!user || !profile || profile.friendshipStatus !== "pending_sent") return;
    setCancelRequestLoading(true);
    try {
      const res = await fetch(`${process.env.API_URL}/account/friend-request/cancel`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ receiver_id: profile.userId }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        trackFriendRequestCancelled({
          user_id: user.userId,
          sender_user_id: user.userId,
          receiver_user_id: profile.userId,
          pathname: location.pathname,
        });
        setProfile((p) => (p ? { ...p, friendshipStatus: "none" as const } : null));
      } else if (data?.error === "not_found") {
        setProfile((p) => (p ? { ...p, friendshipStatus: "none" as const } : null));
      }
    } finally {
      setCancelRequestLoading(false);
    }
  }, [user, profile?.friendshipStatus, profile?.userId, location.pathname]);

  const isOwnProfile = Boolean(user && profile && Number(profile.userId) === Number(user.userId));
  const country = profile?.country?.trim() || "";
  const favourites = profile?.favourites ? normalizeFavourites(profile.favourites) : null;
  const joinDateShort = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  const games = profile?.games;
  const spin = gameValues(games, "wheel-spin");
  const wheelTrivia = gameValues(games, "wheel-trivia");
  const wheelLegacyLastwon = spin.lastwon + wheelTrivia.lastwon;
  const career = profile?.career;
  const careerLedger = career ? coercePublicCareer(career) : null;
  const friendsPreview = profile?.friends_preview ?? [];
  const recent = profile?.recent_activity ?? [];

  const legacyMiniGamesPlayed =
    games?.reduce((sum, g) => {
      if (g.name !== "quickfire-trivia") return sum;
      const p = g.values?.played;
      const n = typeof p === "number" ? p : Number(p);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0) ?? 0;

  const legacyCoinsWonHeader =
    (games?.reduce((sum, g) => {
      const w = g.values?.lastwon;
      const n = typeof w === "number" ? w : Number(w);
      if (g.name === "quickfire-trivia") {
        return sum + (Number.isFinite(n) ? n : 0);
      }
      return sum;
    }, 0) ?? 0) +
    (career?.trader?.profit ?? 0) +
    (career?.squares?.net_winnings ?? 0) +
    (careerLedger?.bullion.net_winnings ?? career?.bullion?.net_winnings ?? 0) +
    (careerLedger?.sixes.net_winnings ?? career?.sixes?.net_winnings ?? 0) +
    (careerLedger?.leagues.net_winnings ?? career?.leagues?.net_winnings ?? 0);

  const totalGamesPlayed =
    careerLedger != null
      ? careerLedger.totals.games_played
      : legacyMiniGamesPlayed +
        (career?.trader?.trades ?? 0) +
        (career?.squares?.played ?? 0) +
        (career?.bullion?.played ?? 0) +
        (career?.sixes?.played ?? 0) +
        (career?.leagues?.played ?? 0);

  const coinsWonDisplay =
    careerLedger != null ? careerLedger.totals.net_coins_won : legacyCoinsWonHeader;

  const favChips: ProfileFavChip[] = [];
  for (const label of parseFavSportsFromStorage(favourites?.favsports)) {
    favChips.push({ key: `sport-${label}`, label });
  }
  for (const slug of parseFavCricketersFromStorage(favourites?.favcricketers)) {
    favChips.push({ key: `cr-${slug}`, label: favCricketerLabel(slug) });
  }
  const iplStoreSlugs = parseFavIplFromStorage(favourites?.faviplteams);
  const nationalStoreSlugs = parseFavCricketIntFromStorage(favourites?.favnationalteams);
  const pickerTeamSlugs = new Set<string>(
    [...iplStoreSlugs, ...nationalStoreSlugs].map(canonicalTeamSlug)
  );
  for (const slug of iplStoreSlugs) {
    favChips.push({
      key: `ipl-${slug}`,
      label: favIplLabel(slug),
      longLabel: favIplLongLabel(slug),
    });
  }
  for (const slug of nationalStoreSlugs) {
    favChips.push({ key: `cint-${slug}`, label: favCricketIntLabel(slug) });
  }
  for (const name of parseFavTeamsFromStorage(favourites?.favcustomcricketteams)) {
    if (pickerTeamSlugs.has(canonicalTeamSlug(name))) continue;
    favChips.push({ key: `team-${slugifyTeamName(name)}`, label: name, longLabel: name });
  }
  for (const slug of parseFavFootballFromStorage(favourites?.favfootballteams)) {
    favChips.push({
      key: `fb-${slug}`,
      label: favFootballLabel(slug),
      longLabel: favFootballLongLabel(slug),
    });
  }

  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
        <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-start gap-x-2 border-b border-white/10 px-3 py-1.5 sm:py-2">
          <div className="col-start-1 mt-1 h-4 w-16 animate-pulse bg-white/12" />
          <div className="col-start-2 flex flex-col items-center gap-3">
            <div className="h-[86px] w-[86px] animate-pulse bg-white/12 sm:h-[92px] sm:w-[92px]" />
            <div className="h-6 w-36 animate-pulse bg-white/12" />
          </div>
          <div className="col-start-3 mt-1 h-8 w-8 justify-self-end animate-pulse bg-white/12" />
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-3 py-3">
          <section>
            <div className="mb-2 h-3 w-24 animate-pulse bg-white/12" />
            <div className="grid grid-cols-2 gap-2">
              <div className="h-16 animate-pulse bg-white/10" />
              <div className="h-16 animate-pulse bg-white/10" />
            </div>
          </section>
          <section>
            <div className="mb-2 h-3 w-20 animate-pulse bg-white/12" />
            <div className="grid grid-cols-2 gap-2">
              <div className="h-24 animate-pulse bg-white/10" />
              <div className="h-24 animate-pulse bg-white/10" />
              <div className="h-24 animate-pulse bg-white/10" />
              <div className="h-24 animate-pulse bg-white/10" />
            </div>
          </section>
          <section>
            <div className="mb-2 h-3 w-24 animate-pulse bg-white/12" />
            <div className="space-y-1.5">
              <div className="h-[46px] animate-pulse bg-white/10" />
              <div className="h-[46px] animate-pulse bg-white/10" />
              <div className="h-[46px] animate-pulse bg-white/10" />
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <ProfileUnavailablePanel
        message={privateMessage ?? "Profile not found."}
        onClose={onClose}
        identity={restrictedPreview}
      />
    );
  }

  if (isNonUserPublicProfile(profile)) {
    return (
      <ProfileUnavailablePanel
        message="This profile is not visible to others."
        onClose={onClose}
        identity={{
          player_name: profile.player_name,
          username: profile.username,
          profile_pic: profile.profile_pic,
          avatar_url: profile.avatar_url,
          avatar_color: profile.avatar_color,
          first_letter: profile.first_letter,
        }}
      />
    );
  }

  const showRealName = canSeeDisplayName({
    isOwnProfile,
    friendshipStatus: profile.friendshipStatus,
  });
  const playerNameLabel = playerHandleName(profile);
  const realNameLabel = showRealName ? optionalDisplayName(profile) : null;
  const displayName = playerNameLabel.toUpperCase();
  const rawPhotoUrl = resolveAvatarUrl(profile);
  const resolvedHeroSrc = toProfileHeroAvatarUrl(rawPhotoUrl);
  const rawHadPhoto = Boolean(rawPhotoUrl && String(rawPhotoUrl).trim());
  const displayNameForAvatar = playerNameLabel;
  /** Chips / activity rows — same URL as hero (letter fallback only on hero). */
  const avatarSrc = resolvedHeroSrc;
  const isOnline = profileIsOnlineNow(profile);
  const profileBlocksNewFriendRequests =
    profile.accept_friend_requests === false &&
    (profile.friendshipStatus === "none" || profile.friendshipStatus == null);

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
      <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-start gap-x-2 border-b border-white/10 px-3 py-1.5 sm:py-2">
        <div className="col-start-1 flex min-w-0 flex-col items-start gap-2 self-start">
          <span
            className={`flex items-center gap-1.5 font-anton text-[8px] font-normal uppercase leading-[12px] tracking-normal [leading-trim:none] ${
              isOnline ? "text-[#D2FF00]" : "text-white/55"
            }`}
          >
            <span
              className={`h-[7px] w-[7px] shrink-0 rounded-full ${isOnline ? "bg-[#D2FF00]" : "bg-white/45"}`}
              aria-hidden
            />
            {isOnline ? "ONLINE" : "OFFLINE"}
          </span>
          {!isOwnProfile && !isNonUserPublicProfile(profile) ? (
            user ? (
              profile.friendshipStatus === "friends" ? (
                <div className="mt-0 flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!canChallengeFriend) return;
                      const friend: SquaresChallengeFriend = {
                        userId: profile.userId,
                        player_name: profile.player_name,
                        display_name: profile.display_name,
                        username: profile.username,
                      };
                      trackChallengeFriendClicked({
                        user_id: user?.userId,
                        cta_location: "public_profile",
                        pathname: location.pathname,
                      });
                      const availableCount =
                        Number(isBullionEnabled) +
                        Number(liveSquaresForChallenge) +
                        Number(showLeaguesChallenge);
                      if (availableCount === 1) {
                        if (isBullionEnabled) {
                          setBullionContestFriend(friend);
                          return;
                        }
                        if (liveSquaresForChallenge) {
                          setSquaresChallengeFriend(friend);
                          return;
                        }
                        openCreateLeagueModal({ challengeFriendUserId: friend.userId });
                        return;
                      }
                      setChallengeGamePicker({
                        friend,
                        anchorRect: e.currentTarget.getBoundingClientRect(),
                      });
                    }}
                    disabled={!canChallengeFriend}
                    title={!canChallengeFriend ? "No challengeable games right now" : undefined}
                    className="h-8 shrink-0 cursor-default rounded-none bg-[#D2FF00] px-3 font-anton text-[10px] font-normal uppercase tracking-wide text-black hover:brightness-95 disabled:cursor-default disabled:bg-white/15 disabled:text-white/45 disabled:hover:bg-white/15"
                  >
                    CHALLENGE
                  </button>
                  <div className="relative z-20 flex shrink-0 items-center gap-0.5" data-public-friend-actions>
                    {profileFriendMuted ? (
                      <button
                        type="button"
                        onClick={() => setConfirmFriendProfileAction("unmute")}
                        disabled={muteFriendLoading}
                        className="flex h-8 shrink-0 items-center justify-center rounded-none pr-0.5 enabled:hover:opacity-90 disabled:opacity-50"
                        title="Unmute friend"
                        aria-label="Unmute friend"
                      >
                        <img
                          src="/img/mute-red.svg"
                          alt=""
                          className="h-4 w-4 object-contain"
                          width={16}
                          height={16}
                          decoding="async"
                        />
                      </button>
                    ) : null}
                    <div className="relative" data-public-friend-actions>
                      <button
                        type="button"
                        onClick={() => setFriendProfileMenuOpen((o) => !o)}
                        disabled={removeFriendLoading || muteFriendLoading}
                        className="flex h-[24px] min-w-[24px] items-center justify-center border-0 bg-white/10 px-1 text-white hover:bg-white/20 disabled:opacity-60"
                        aria-label="More actions"
                      >
                        <ThreeDotsCircleIcon className="text-white" size={14} />
                      </button>
                      {friendProfileMenuOpen ? (
                        <div
                          data-public-friend-actions
                          className="absolute left-0 top-full z-30 mt-1.5 w-[140px] border border-[#7f5aca] bg-[#7944c3] p-1 shadow-[0_8px_22px_rgba(8,2,18,0.6)]"
                        >
                          {profileFriendMuted ? (
                            <button
                              type="button"
                              onClick={() => {
                                setFriendProfileMenuOpen(false);
                                setConfirmFriendProfileAction("unmute");
                              }}
                              disabled={muteFriendLoading}
                              className="mb-1 flex h-9 w-full items-center justify-start gap-2 bg-[#552ca6] px-3 font-anton text-[14px] text-white hover:bg-[#6138b0] disabled:opacity-60"
                            >
                              <img src="/img/mute.svg" alt="" className="h-4 w-4 shrink-0 opacity-90" />
                              <span>UNMUTE</span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setFriendProfileMenuOpen(false);
                                setConfirmFriendProfileAction("mute");
                              }}
                              disabled={muteFriendLoading}
                              className="mb-1 flex h-9 w-full items-center justify-start gap-2 bg-[#552ca6] px-3 font-anton text-[14px] text-white hover:bg-[#6138b0] disabled:opacity-60"
                            >
                              <img src="/img/mute.svg" alt="" className="h-4 w-4 shrink-0" />
                              <span>MUTE</span>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setFriendProfileMenuOpen(false);
                              setConfirmFriendProfileAction("remove");
                            }}
                            disabled={removeFriendLoading}
                            className="flex h-9 w-full items-center justify-start gap-2 bg-[#552ca6] px-3 font-anton text-[14px] text-white hover:bg-[#6138b0] disabled:opacity-60"
                          >
                            <img src="/img/x.svg" alt="" className="h-4 w-4 shrink-0" />
                            <span>REMOVE</span>
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : profile.friendshipStatus === "pending_sent" ? (
                <button
                  type="button"
                  onClick={handleCancelFriendRequest}
                  disabled={cancelRequestLoading}
                  className="mt-0 h-8 shrink-0 rounded-none bg-white px-3 font-anton text-[10px] font-normal uppercase tracking-wide text-[#1B062F] hover:bg-white/90 disabled:opacity-60"
                >
                  {cancelRequestLoading ? "…" : "CANCEL REQUEST"}
                </button>
              ) : profile.friendshipStatus === "pending_received" ? (
                <button
                  type="button"
                  onClick={handleAcceptFriendRequest}
                  disabled={acceptRequestLoading}
                  className="mt-0 h-8 shrink-0 rounded-none bg-[#D2FF00] px-3 font-anton text-[10px] font-normal uppercase tracking-wide text-black hover:brightness-95 disabled:opacity-60"
                >
                  {acceptRequestLoading ? "…" : "ACCEPT REQUEST"}
                </button>
              ) : profileBlocksNewFriendRequests ? null : (
                <button
                  type="button"
                  onClick={handleAddFriend}
                  disabled={addFriendState === "loading"}
                  className="mt-0 inline-flex h-8 min-h-8 shrink-0 items-center justify-center rounded-none border-0 bg-white px-3 font-anton text-[10px] font-normal uppercase tracking-wide text-[#1B062F] hover:bg-white/90 disabled:opacity-60"
                >
                  {addFriendState === "loading"
                    ? "…"
                    : addFriendState === "error"
                      ? "TRY AGAIN"
                      : "ADD TO FRIENDS"}
                </button>
              )
            ) : profileBlocksNewFriendRequests ? null : (
              <button
                type="button"
                onClick={handleAddFriend}
                className="mt-0 inline-flex h-8 min-h-8 shrink-0 items-center justify-center rounded-none border-0 bg-white px-3 font-anton text-[10px] font-normal uppercase tracking-wide text-[#1B062F] hover:bg-white/90"
              >
                ADD TO FRIENDS
              </button>
            )
          ) : null}
        </div>
        <div className="col-start-2 flex max-w-[min(100%,11rem)] flex-col items-center gap-3 justify-self-center self-start sm:max-w-[13rem]">
          <div className="relative shrink-0">
            <ProfileAvatarHeroWithFallback
              resolvedSrc={resolvedHeroSrc}
              rawHadPhoto={rawHadPhoto}
              displayName={displayNameForAvatar}
              avatarColor={profile.avatar_color}
              firstLetter={profile.first_letter}
              syntheticBotLetterTile={Boolean(profile.synthetic_bot)}
              xpWeekCrown={profile.xp_week_crown}
              avatarFrameUrl={profile.avatar_frame_url}
              avatarFrameSmallUrl={profile.avatar_frame_small_url}
            />
          </div>
          <ProfileNameStack
            className="w-full max-w-full"
            playerName={playerNameLabel}
            displayName={realNameLabel}
          />
        </div>
        <div className="col-start-3 justify-self-end self-start">
          <ModalCloseButton
            onClick={onClose}
            ariaLabel="Close profile"
            className="h-11 w-11 shrink-0 translate-x-1.5 -translate-y-1.5 rounded-none hover:bg-white/10 hover:opacity-100"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-8 pt-3">
        <div className="mb-3 mt-2 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-none bg-[#652DAF] px-1 py-2">
            <p className="font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal text-white/40 [leading-trim:none]">
              Joined
            </p>
            <p className="mt-1.5 font-anton text-[15px] font-normal uppercase leading-[12px] tracking-normal text-white [leading-trim:none]">
              {joinDateShort ?? "—"}
            </p>
          </div>
          <div className="rounded-none bg-[#652DAF] px-1 py-2">
            <p className="font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal text-white/40 [leading-trim:none]">
              Games played
            </p>
            <p className="mt-1.5 font-anton text-[15px] font-normal uppercase leading-[12px] tracking-normal text-white [leading-trim:none]">
              {formatCoins(totalGamesPlayed)}
            </p>
          </div>
          <div className="rounded-none bg-[#652DAF] px-1 py-2">
            <p className="font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal text-white/40 [leading-trim:none]">
              Game score
            </p>
            <p className="mt-1.5 flex items-center justify-center gap-1 font-anton text-[15px] font-normal uppercase leading-[12px] tracking-normal text-white [leading-trim:none]">
              <img src="/assets/currency-gold-ball.png" alt="" className="h-3.5 w-3.5 shrink-0" />
              {formatCoins(Math.round(coinsWonDisplay))}
            </p>
          </div>
        </div>

        {favChips.length > 0 && <ProfileFavouritesStrip chips={favChips} />}

        {!career ? (
          <p className="mb-4 text-center text-xs text-white/50">This player&apos;s detailed game statistics are private.</p>
        ) : null}

        {career && careerLedger && (
          <CareerStatsStack
            xp={careerLedger.xp ?? null}
            showXpHistoryLink={false}
            trader={{
              played: careerLedger.trader.trades,
              won: careerLedger.trader.won,
              totalWinnings: careerLedger.trader.total_winnings,
              currentStreak: careerLedger.trader.current_win_streak,
              bestStreak: careerLedger.trader.record_win_streak,
              biggestWin: careerLedger.trader.biggest_win,
            }}
            squares={{
              played: careerLedger.squares.played,
              won: careerLedger.squares.won,
              totalWinnings: careerLedger.squares.net_winnings,
            }}
            bullion={{
              played: careerLedger.bullion.played,
              won: careerLedger.bullion.won,
              totalWinnings: careerLedger.bullion.net_winnings,
            }}
            sixes={{
              played: careerLedger.sixes.played,
              totalWinnings: careerLedger.sixes.net_winnings,
              biggestWin: careerLedger.sixes.biggest_win,
            }}
            leagues={{
              played: careerLedger.leagues.played,
              won: careerLedger.leagues.won,
              totalWinnings: careerLedger.leagues.net_winnings,
            }}
            quickfire={{
              plays: careerLedger.mini_games["quickfire-trivia"].played,
              correct: careerLedger.mini_games["quickfire-trivia"].correct_answers,
              totalWinnings: careerLedger.mini_games["quickfire-trivia"].net_coins,
            }}
            formatCoins={formatCoins}
          />
        )}

        {friendsPreview.length > 0 && (
          <section className="mb-4 flex flex-col items-center">
            <h2 className="mb-2 flex items-center justify-center gap-0.5 font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal [leading-trim:none]">
              <span className="shrink-0 text-white">Friends</span>
              <span className="text-white/40">({friendsPreview.length})</span>
            </h2>
            <div
              className="w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain pb-1 hide-scrollbar snap-x snap-mandatory touch-pan-x scroll-pl-3 scroll-pr-3"
              aria-label="Friends — swipe or scroll horizontally"
            >
              <div className="mx-auto flex w-max max-w-none flex-nowrap items-center gap-2">
                {friendsPreview.map((f) => (
                  <ProfileLink
                    key={f.userId}
                    to={
                      f.player_id
                        ? `/profile?p=${encodeURIComponent(f.player_id)}`
                        : `/profile?u=${encodeURIComponent(String(f.userId))}`
                    }
                    state={profileLinkBackground}
                    className="group shrink-0 snap-start"
                  >
                    <ProfileAvatar
                      variant="chip"
                      src={resolveAvatarUrl({ profile_pic: f.profile_pic, avatar_url: f.avatar_url }) ?? ""}
                      displayName={f.player_name || f.username}
                      xpWeekCrown={f.xp_week_crown}
                      avatarFrameUrl={f.avatar_frame_url}
                      avatarFrameSmallUrl={f.avatar_frame_small_url}
                    />
                  </ProfileLink>
                ))}
              </div>
            </div>
          </section>
        )}

        {isOwnProfile && (
          <section className="rounded-none border border-white/10 bg-white/5 p-3">
            <h2 className="mb-2 font-anton text-sm uppercase text-white">Details</h2>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between gap-2 border-b border-white/5 pb-2">
                <span className="text-white/60">Country</span>
                <span className="text-white">{getCountryNameByCode(country) || country || "—"}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-white/60">Favourite Sports</span>
                <span className="max-w-[60%] text-right text-white">
                  {parseFavSportsFromStorage(favourites?.favsports).join(", ") || "—"}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-white/60">Favourite Cricket players</span>
                <span className="max-w-[60%] text-right text-white">
                  {parseFavCricketersFromStorage(favourites?.favcricketers)
                    .map((s) => favCricketerLabel(s))
                    .join(", ") || "—"}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-white/60">Club teams</span>
                <span className="max-w-[60%] text-right text-white">
                  {[
                    ...parseFavIplFromStorage(favourites?.faviplteams).map((s) => favIplLabel(s)),
                    ...parseFavTeamsFromStorage(favourites?.favcustomcricketteams)
                      .filter((name) => resolveCanonicalTeam(name).category !== "national")
                      .filter(
                        (name) =>
                          !pickerTeamSlugs.has(canonicalTeamSlug(name))
                      ),
                  ].join(", ") || "—"}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-white/60">International cricket</span>
                <span className="max-w-[60%] text-right text-white">
                  {[
                    ...parseFavCricketIntFromStorage(favourites?.favnationalteams).map((s) =>
                      favCricketIntLabel(s)
                    ),
                    ...parseFavTeamsFromStorage(favourites?.favcustomcricketteams)
                      .filter((name) => resolveCanonicalTeam(name).category === "national")
                      .filter((name) => !pickerTeamSlugs.has(canonicalTeamSlug(name))),
                  ].join(", ") || "—"}
                </span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-white/60">Favourite Football teams</span>
                <span className="max-w-[60%] text-right text-white">
                  {parseFavFootballFromStorage(favourites?.favfootballteams)
                    .map((s) => favFootballLabel(s))
                    .join(", ") || "—"}
                </span>
              </div>
            </div>
          </section>
        )}

        {user &&
          (isOwnProfile || profile?.friendshipStatus === "friends") &&
          !isNonUserPublicProfile(profile) && (
          <section className="mb-2 mt-6">
            <h2 className="mb-3 text-center font-anton text-[11px] font-normal uppercase leading-[12px] tracking-wide text-white [leading-trim:none]">
              Activity
            </h2>
            <FriendActivityTimeline
              digests={friendFeedDigests}
              liveFriends={friendLiveFriends}
              myTrades={friendFeedMyTrades}
              mySquares={friendFeedMySquares}
              notifications={[]}
              profileModalState={profileLinkBackground}
              loading={friendFeedLoading}
              visibleCount={10}
              surface="profile"
              enableInfiniteScroll
              infiniteScrollStep={10}
              isOwnProfileView={isOwnProfile}
              hasMoreServerRows={friendFeedHasMore}
              serverLoadingMore={friendFeedLoadingMore}
              onReachEnd={() => {
                if (friendFeedLoadingMore || !friendFeedHasMore) return;
                setFriendFeedResultsLimit((prev) => prev + 10);
              }}
              onProfileLinkClick={() => onClose()}
              emptyMessage={
                isOwnProfile ? "No recent activity yet." : "No shared activity yet."
              }
            />
          </section>
        )}

        {!isOwnProfile &&
          !(user && profile?.friendshipStatus === "friends") &&
          !isNonUserPublicProfile(profile) &&
          recent.length > 0 && (
          <section className="mb-2 mt-6">
            <h2 className="mb-3 text-center font-anton text-[11px] font-normal uppercase leading-[12px] tracking-wide text-white [leading-trim:none]">
              Recent activity
            </h2>
            <ul className="flex flex-col gap-2">
              {recent.map((ev, i) => {
                const kind = ev.kind ?? "trader";
                if (kind === "xp") {
                  const rel = formatActivityRelative(ev.settled_at ?? ev.created_at ?? null);
                  return (
                    <li
                      key={`xp-${String(ev.settled_at ?? "")}-${String(ev.xp_reason ?? "")}-${i}`}
                      className="flex items-center gap-2 rounded-none border border-white/10 bg-[#381663]/40 px-2 py-2.5"
                    >
                      <ProfileAvatar
                        variant="chip"
                        src={avatarSrc}
                        displayName={displayNameForAvatar}
                        avatarColor={profile.avatar_color}
                        firstLetter={profile.first_letter}
                        xpWeekCrown={profile.xp_week_crown}
                        avatarFrameUrl={profile.avatar_frame_url}
                        avatarFrameSmallUrl={profile.avatar_frame_small_url}
                        className="shrink-0"
                      />
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-[11px] leading-snug text-white">
                          <span className="font-bold uppercase">{displayName}</span>
                          <span className="font-normal"> earned </span>
                          <span className="inline-flex items-center gap-0.5 self-center font-bold leading-none">
                            <span className="tabular-nums">{formatXp(ev.xp_amount ?? 0)}</span>
                            <img src={XP_ICON_SRC} alt="" className="h-3 w-3 shrink-0 object-contain" aria-hidden />
                          </span>
                          <span className="font-normal">{formatXpHistoryReasonDetail(ev.xp_reason)}.</span>
                        </div>
                      </div>
                      <span className="shrink-0 self-center text-[10px] text-white/55">{rel}</span>
                    </li>
                  );
                }
                const oc = String(ev.outcome ?? "").toUpperCase();
                const isActive = kind === "squares_active";
                const rel = formatActivityRelative(
                  isActive ? (ev.created_at ?? null) : (ev.settled_at ?? null)
                );
                const coinSpan = (
                  <span className="inline-flex items-center gap-0.5 self-center whitespace-nowrap leading-none">
                    <img
                      src="/assets/currency-gold-ball.png"
                      alt=""
                      className="h-3 w-3 shrink-0 object-contain"
                      aria-hidden
                    />
                    <span className="tabular-nums">{formatCoins(Math.abs(Math.round(ev.profit)))}</span>
                  </span>
                );

                let middle: React.ReactNode;
                if (isActive) {
                  middle = (
                    <>
                      <span className="font-bold uppercase">{displayName}</span>
                      <span className="font-normal"> is currently playing on </span>
                      <span className="inline-flex items-center gap-1 self-center font-bold uppercase leading-none">
                        <img
                          src={CAREER_STAT_ICONS.squares}
                          alt=""
                          className="h-3.5 w-3.5 shrink-0 object-contain"
                          aria-hidden
                        />
                        SQUARES
                      </span>
                    </>
                  );
                } else if (kind === "squares") {
                  if (oc === "VOID") {
                    middle = (
                      <>
                        <span className="font-bold uppercase">{displayName}</span>
                        <span className="font-normal"> voided on </span>
                        <span className="inline-flex items-center gap-1 self-center font-bold uppercase leading-none">
                          <img
                            src={CAREER_STAT_ICONS.squares}
                            alt=""
                            className="h-3.5 w-3.5 shrink-0 object-contain"
                            aria-hidden
                          />
                          SQUARES
                        </span>
                      </>
                    );
                  } else {
                    const won = oc === "WIN";
                    middle = (
                      <>
                        <span className="font-bold uppercase">{displayName}</span>
                        <span className="font-normal">{won ? " won " : " lost "}</span>
                        {coinSpan}
                        <span className="font-normal"> on </span>
                        <span className="inline-flex items-center gap-1 self-center font-bold uppercase leading-none">
                          <img
                            src={CAREER_STAT_ICONS.squares}
                            alt=""
                            className="h-3.5 w-3.5 shrink-0 object-contain"
                            aria-hidden
                          />
                          SQUARES
                        </span>
                      </>
                    );
                  }
                } else if (kind === "sixes") {
                  middle = (
                    <>
                      <span className="font-bold uppercase">{displayName}</span>
                      <span className="font-normal"> won </span>
                      {coinSpan}
                      <span className="font-normal"> on </span>
                      <span className="inline-flex items-center gap-1 self-center font-bold uppercase leading-none">
                        <img
                          src={CAREER_STAT_ICONS.sixes}
                          alt=""
                          className="h-3.5 w-3.5 shrink-0 object-contain"
                          aria-hidden
                        />
                        SIXES
                      </span>
                    </>
                  );
                } else if (oc === "VOID") {
                  middle = (
                    <>
                      <span className="font-bold uppercase">{displayName}</span>
                      <span className="font-normal"> voided on </span>
                      <span className="inline-flex items-center gap-1 self-center font-bold uppercase leading-none">
                        <img
                          src={CAREER_STAT_ICONS.trader}
                          alt=""
                          className="h-3.5 w-3.5 shrink-0 object-contain"
                          aria-hidden
                        />
                        TRADER
                      </span>
                    </>
                  );
                } else {
                  const won = oc === "WIN";
                  middle = (
                    <>
                      <span className="font-bold uppercase">{displayName}</span>
                      <span className="font-normal">{won ? " won " : " lost "}</span>
                      {coinSpan}
                      <span className="font-normal"> on </span>
                      <span className="inline-flex items-center gap-1 self-center font-bold uppercase leading-none">
                        <img
                          src={CAREER_STAT_ICONS.trader}
                          alt=""
                          className="h-3.5 w-3.5 shrink-0 object-contain"
                          aria-hidden
                        />
                        TRADER
                      </span>
                    </>
                  );
                }

                const matchIdTrim = ev.match_id?.trim() ?? "";
                const rawMatchName = ev.match_name?.trim();
                const hasReadableMatchName = hasReadableActivityMatchName(rawMatchName, matchIdTrim);
                const matchDisplayLabel = hasReadableMatchName
                  ? formatMatchNameAbbreviated(rawMatchName) || null
                  : null;
                const overNum =
                  typeof ev.over_number === "number" &&
                  Number.isFinite(ev.over_number) &&
                  ev.over_number > 0
                    ? ev.over_number
                    : null;
                const isSquaresKind = kind === "squares" || kind === "squares_active";
                const isSixesKind = kind === "sixes";
                let matchTo: string | null = null;
                if (!isSixesKind) {
                  if (isSquaresKind) {
                    if (isActive) {
                      matchTo = matchIdTrim
                        ? `/squares?match=${encodeURIComponent(matchIdTrim)}`
                        : "/squares";
                    } else if (matchIdTrim) {
                      matchTo = buildMatchCompleteHref(matchIdTrim, ev.sport);
                    } else {
                      matchTo = "/squares";
                    }
                  } else if (kind === "trader") {
                    matchTo = traderActivityMatchHref(matchIdTrim, rawMatchName, ev.sport);
                  }
                }
                const matchDetail =
                  matchDisplayLabel ? (
                    <div className="mt-0.5 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] leading-tight text-white/80">
                      <span className="min-w-0 max-w-full truncate">{matchDisplayLabel}</span>
                      {isSquaresKind && overNum != null ? (
                        <span className="shrink-0 whitespace-nowrap text-white/55">Over {overNum}</span>
                      ) : null}
                    </div>
                  ) : null;

                return (
                  <li
                    key={`${kind}-${String(ev.settled_at ?? "")}-${String(ev.created_at ?? "")}-${String(ev.match_id ?? "")}-${i}`}
                    className={`flex items-center gap-2 rounded-none border px-2 py-2.5 ${
                      isActive
                        ? "border-[#D2FF00] bg-[#5a2fa3]/80"
                        : "border-white/10 bg-[#381663]/40"
                    }`}
                  >
                    <ProfileAvatar
                      variant="chip"
                      src={avatarSrc}
                      displayName={displayNameForAvatar}
                      avatarColor={profile.avatar_color}
                      firstLetter={profile.first_letter}
                      xpWeekCrown={profile.xp_week_crown}
                      avatarFrameUrl={profile.avatar_frame_url}
                      avatarFrameSmallUrl={profile.avatar_frame_small_url}
                      className="shrink-0"
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-[11px] leading-snug text-white">
                        {middle}
                      </div>
                      {matchDetail}
                    </div>
                    <div className="flex shrink-0 flex-row items-center justify-end gap-2">
                      {rel ? (
                        <span className="inline-flex items-center whitespace-nowrap text-[10px] leading-none text-white/45">
                          {rel}
                        </span>
                      ) : null}
                      {matchTo ? (
                        <ProfileLink
                          to={matchTo}
                          state={profileLinkBackground}
                          onClick={(e) => {
                            e.preventDefault();
                            navigateFromProfileOverlay(matchTo);
                          }}
                          className={ACTIVITY_ACTION_BUTTON}
                        >
                          MATCH
                        </ProfileLink>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>

      {challengeGamePicker ? (
        <ChallengeGameSelectPopover
          anchorRect={challengeGamePicker.anchorRect}
          onClose={() => setChallengeGamePicker(null)}
          onPickBullion={() => {
            const f = challengeGamePicker.friend;
            trackChallengeGameSelected({
              user_id: user?.userId,
              game_type: "bullion",
              cta_location: "public_profile",
              pathname: location.pathname,
            });
            setBullionContestFriend(f);
          }}
          onPickSquares={() => {
            const f = challengeGamePicker.friend;
            trackChallengeGameSelected({
              user_id: user?.userId,
              game_type: "squares",
              cta_location: "public_profile",
              pathname: location.pathname,
            });
            setSquaresChallengeFriend(f);
          }}
          onPickLeagues={() => {
            const f = challengeGamePicker.friend;
            trackChallengeGameSelected({
              user_id: user?.userId,
              game_type: "leagues",
              cta_location: "public_profile",
              pathname: location.pathname,
            });
            openCreateLeagueModal({ challengeFriendUserId: f.userId });
          }}
          showBullion={isBullionEnabled}
          showSquares={liveSquaresForChallenge}
          showLeagues={showLeaguesChallenge}
        />
      ) : null}

      <SquaresChallengeFriendModal
        isOpen={squaresChallengeFriend != null}
        onRequestClose={() => setSquaresChallengeFriend(null)}
        friend={squaresChallengeFriend}
        analyticsEntryPoint="public_profile"
        onOpenMatch={openChallengedMatchFromProfile}
      />

      {isBullionEnabled ? (
        <BullionPrivateContestsFriendModal
          isOpen={bullionContestFriend != null}
          onRequestClose={() => setBullionContestFriend(null)}
          friend={bullionContestFriend}
          analyticsEntryPoint="public_profile"
          onOpenMatch={openChallengedMatchFromProfile}
        />
      ) : null}

      {confirmFriendProfileAction ? (
        <AreYouSure
          isOpen={true}
          onRequestClose={closeConfirmFriendProfileAction}
          contentLabel="Confirm friend action"
          children={
            <div className="flex flex-col items-center gap-4 p-5">
              <p className="text-center font-anton text-[22px] text-white">
                {confirmFriendProfileAction === "unmute" ? (
                  <>
                    ARE YOU SURE YOU WANT TO UNMUTE{" "}
                    {playerHandleName(profile).toUpperCase()}?
                  </>
                ) : (
                  <>
                    Are you sure you want to{" "}
                    {confirmFriendProfileAction === "remove" ? "remove" : confirmFriendProfileAction}{" "}
                    {playerHandleName(profile).toUpperCase()}?
                  </>
                )}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeConfirmFriendProfileAction}
                  className="flex h-9 min-w-[112px] items-center justify-center rounded-none border-2 border-solid border-lime bg-transparent px-4 font-anton text-[14px] text-lime transition-colors hover:border-limehover hover:text-limehover"
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  onClick={confirmSelectedFriendProfileAction}
                  disabled={
                    confirmFriendProfileAction === "remove"
                      ? removeFriendLoading
                      : muteFriendLoading
                  }
                  className="flex h-9 min-w-[112px] items-center justify-center rounded-none bg-lime px-4 font-anton text-[14px] text-main transition-colors hover:bg-limehover disabled:opacity-60"
                >
                  YES
                </button>
              </div>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
